import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { isRateLimitedError, isServerError } from "@/lib/api/core";
import { getPublicBootstrap } from "@/lib/public-bootstrap";

/**
 * Single source of truth for sending someone to `/auth/login`.
 *
 * The route validates seven search keys and TanStack's typed navigate/<Link>
 * want the whole shape spelled out, so every call site used to repeat all
 * seven — most of them purely to write `undefined`. That is how `/$owner`
 * shipped as the one owner-level route with no sign-in redirect at all: there
 * was nothing to reuse, only a literal to copy.
 */

type SignInSearchOptions = {
	callbackUrl?: string;
	intent?: "sign-in" | "sign-up";
	invitationOrg?: string;
};

export function signInSearch(options: SignInSearchOptions = {}) {
	return {
		callbackUrl: options.callbackUrl,
		mode: undefined,
		error: undefined,
		errorDescription: undefined,
		loginHint: undefined,
		invitationOrg: options.invitationOrg,
		intent: options.intent,
	};
}

/**
 * Where sign-in should return. A callback that is itself `/auth/login`
 * is unwrapped, so a sign-in control cannot nest another login URL around
 * the page the person was already on.
 */
export function callbackAfterSignIn(url: string): string {
	let current = url;
	for (let depth = 0; depth < 4; depth += 1) {
		const queryIndex = current.indexOf("?");
		const path = queryIndex === -1 ? current : current.slice(0, queryIndex);
		const parts = path.split("/").filter(Boolean);
		const authIndex = parts[0] === "auth" ? 0 : parts[1] === "auth" ? 1 : -1;
		if (authIndex < 0 || parts[authIndex + 1] !== "login") {
			return current.startsWith("/") && !current.startsWith("//")
				? current
				: "/";
		}
		const params = new URLSearchParams(
			queryIndex === -1 ? "" : current.slice(queryIndex + 1),
		);
		const inner = params.get("callbackUrl");
		if (inner == null || !inner.startsWith("/") || inner.startsWith("//"))
			return "/";
		current = inner;
	}
	return "/";
}

/**
 * Where the user is now, for a post-sign-in bounce back.
 *
 * Deliberately `window.location` and not the router: on a per-org subdomain
 * the router works in `/${owner}`-prefixed paths while the URL bar — and so
 * the path the browser re-requests after login — stays unprefixed. Reading the
 * router here would send `acme.lobu.ai` users back to `/acme/...`, which the
 * subdomain history would then prefix a second time.
 */
export function currentCallbackUrl(): string {
	return callbackAfterSignIn(window.location.pathname + window.location.search);
}

/** Sign-in search for a public workspace. The name is the display name. */
export function publicJoinSignInSearch(workspaceName: string | undefined) {
	return signInSearch({
		callbackUrl: currentCallbackUrl(),
		intent: "sign-in",
		invitationOrg: workspaceName,
	});
}

/**
 * Heading and detail for a login that names a workspace.
 * An email invitation sets `loginHint` and keeps the invitation sentence.
 * A public-workspace join has the display name and no hint.
 */
export function loginInvitationCopy(
	invitationOrg: string | undefined,
	loginHint: string | undefined,
): { title: string; detail: string } | null {
	if (!invitationOrg) return null;
	return {
		title: `Sign in to join ${invitationOrg}`,
		detail: loginHint
			? "Sign in with the email your invitation was sent to."
			: `This sign-in is for the ${invitationOrg} workspace.`,
	};
}

type AnonymousSignInRedirectOptions = {
	/**
	 * True once the page has nothing to show a signed-out visitor. Callers must
	 * settle their own loading state first, and must not set this for a server
	 * or transport failure — an outage is not an authz problem.
	 */
	active: boolean;
	/**
	 * Owner slug. Skips the redirect when SSR injected a public bootstrap for
	 * this owner, which it only does when the org's visibility is 'public'.
	 */
	owner?: string;
};

/**
 * The ONE decision every anonymous gate asks: does this caller get sent to
 * sign-in?
 *
 * A missing session and nothing to show is normally "log in". Two failures are
 * not: an outage (5xx/transport) would read as "you are not allowed here", and a
 * rate limit would hide the only actionable fact — come back in N seconds —
 * behind a login form that cannot shorten the wait. Both must render their own
 * state instead, which is why this lives here rather than being re-derived (and
 * re-forgotten) at each gate: the owner LAYOUT gate redirected a rate-limited
 * visitor to login before the page gate that knew better ever ran.
 */
export function shouldRedirectAnonymousToSignIn(args: {
	/** The caller's loading state has settled. */
	settled: boolean;
	authReady: boolean;
	session: unknown;
	/** Something to show: a resolved workspace/entity. */
	resolved: boolean;
	error: unknown;
}): boolean {
	if (!args.settled || !args.authReady || args.session || args.resolved) {
		return false;
	}
	if (isServerError(args.error) || isRateLimitedError(args.error)) return false;
	return true;
}

/**
 * Send a signed-out visitor to sign-in, returning them here afterwards.
 *
 * Returns whether a redirect is in flight so the caller can render nothing
 * instead of flashing its own error state on the way out.
 */
export function useAnonymousSignInRedirect({
	active,
	owner,
}: AnonymousSignInRedirectOptions): boolean {
	const navigate = useNavigate();
	const isPublicOrg = !!owner && getPublicBootstrap()?.ownerSlug === owner;
	const redirecting = active && !isPublicOrg;

	useEffect(() => {
		if (!redirecting) return;
		void navigate({
			to: "/auth/login",
			search: signInSearch({
				callbackUrl: currentCallbackUrl(),
				intent: "sign-in",
			}),
			replace: true,
		});
	}, [redirecting, navigate]);

	return redirecting;
}
